import React, { useEffect, useState } from 'react';
import { FormControl, InputLabel, MenuItem, Checkbox, ListItemText, Button, Chip, Box, Typography, FormControlLabel, Select } from '@mui/material';
import { ECommerceActions } from '../../../Services/_actions/ECommerce/ecommerce.action.js';
import { orderFiltersAtom, orderStatusAtom, paymentStatusListAtom } from '../../../Services/_states/index.js';
import { useRecoilState, useRecoilValue, useResetRecoilState } from 'recoil';
import { dateFormate } from '../../../utils/helper.jsx';
import SearchableDropdown from '../../FlowBuilder/Common/SearchableDropdown/SearchableDropdown.tsx';
import DateRangeFilter from '../../Shared/DateRangeFilter/DateRangeFilter.tsx';

type SelectedFilter = {
  objId: string;
  type: string;
  label: string;
};

const OrdersFilters: React.FC = () => {
  const actions = ECommerceActions();
  const [selectedFilters, setSelectedFilters] = useState<SelectedFilter[]>([]);
  const [orderFilters, setOrderFilters] = useRecoilState(orderFiltersAtom);
  const [channelPartnerData, setChannelPartnerData] = useState<any>({});
  const [cityList, setCityList] = useState<any>({});
  const [channelPartnerFilter, setChannelPartnerFilter] = useState({ page: 0, search: '' });
  const [cityFilters, setCityFilters] = useState({ page: 0, search: '' });
  const orderStatusList = useRecoilValue(orderStatusAtom);
  const paymentStatusList = useRecoilValue(paymentStatusListAtom);
  const resetOrderFilters = useResetRecoilState(orderFiltersAtom);

  // Handle adding/removing filters for checkboxes
  const handleCheckboxChange = (filter: SelectedFilter, type = '', keyName = 'value', labelKey = 'value') => {
    filter.type = type;
    filter.objId = filter[keyName];
    filter.label = filter[labelKey];
    setSelectedFilters((prev) => {
      const isSelected = prev.some(item => item[keyName] === filter[keyName] && item.type === filter.type);
      const updatedFilters = isSelected
        ? prev.filter(item => !(item[keyName] === filter[keyName] && item.type === filter.type))
        : [...prev, filter];
      return updatedFilters;
    });
  };
  const handleSelectSalePartner = (selectedOptions) => {
    const updatedFilters = selectedOptions.map((option) => ({
      label: option["Channel Partner Name"],
      objId: option["Channel Partner Name"],
      type: 'channel_sales_partner',
    }));

    setSelectedFilters((prev) => {
      const filtersWithoutSalesPartner = prev.filter(
        (item) => item.type !== 'channel_sales_partner'
      );
      return [...filtersWithoutSalesPartner, ...updatedFilters];
    });
  };
  const handleSelectCity = (selectedOptions: any[]) => {
    const filtersWithoutCities = selectedFilters?.filter((item) => item.type !== 'cities');

    if (selectedOptions && selectedOptions.length > 0) {
      const updatedFilters = selectedOptions?.map(option => ({
        label: option.city,
        objId: option.city,
        type: 'cities',
      }));

      setSelectedFilters([...filtersWithoutCities, ...updatedFilters]);
    } else {
      setSelectedFilters(filtersWithoutCities);
    }
  };
  const removeFilter = (filter: SelectedFilter) => {
    setSelectedFilters((prev) => prev.filter(item => item.objId !== filter.objId || item.type !== filter.type));
  };
  const handleDateRangeChange = ({ startDate, endDate }: any) => {
    let filterToApply: any = { ...orderFilters };
    if (startDate && endDate) {
      filterToApply.orderStartDate = dateFormate(startDate, 'YYYY-MM-DD');
      filterToApply.orderEndDate = dateFormate(endDate, 'YYYY-MM-DD');
    } else {
      filterToApply.orderStartDate = '';
      filterToApply.orderEndDate = '';
    }
    setOrderFilters(filterToApply);
    fetchOrderList(filterToApply);
  };

  const fetchOrderList = async (data: any) => {
    await actions.getOrderList(data);
  };

  useEffect(() => {
    const result = selectedFilters?.reduce((acc, item) => {
      if (!acc[item.type]) {
        acc[item?.type] = [];
      }
      acc[item?.type].push(item?.objId);
      return acc;
    }, {} as any);

    for (const key in result)
      result[key] = result[key]?.join(',');

    let filterToApply = {};
    if (Object.keys(result).length > 0)
      filterToApply = { ...orderFilters, ...result, page: 0, search: '' };
    else
      filterToApply = { ...orderFilters, page: 0, search: '', payment_status: '', order_status: '', channel_sales_partner: '', cities: '' };

    setOrderFilters(filterToApply);
    fetchOrderList(filterToApply);
  }, [selectedFilters]);

  const getChannelSalesPartners = async (obj: any) => {
    let res = await actions.getChannelSalesPartners(obj);
    console.log("Fetched Channel Sales Partners: ", res); // Log the response

    setChannelPartnerData(res);
  };

  const getOrderCityList = async (obj: any) => {
    let res = await actions.getOrderCityList(obj);

    setCityList(res || {});
  };

  const handleSearchCity = (e: string) => {
    setCityFilters({ ...cityFilters, next_page: 1, search: e });
    getOrderCityList({ ...cityFilters, next_page: 1, search: e });
  };

  const handleSearchSalesPartner = (e: string) => {
    setChannelPartnerFilter({ ...channelPartnerFilter, next_page: 1, search: e });
    getChannelSalesPartners({ ...cityFilters, next_page: 1, search: e });
  };

  useEffect(() => {
    getChannelSalesPartners(channelPartnerFilter);
    getOrderCityList(cityFilters);
  }, []);

  const clearAllFilters = () => {
    resetOrderFilters();
    setSelectedFilters([]);
  };

  return (
    <Box className="orders-filter">
      <Box className="d-flex justify-content-between align-items-center">
        <Typography variant="h6" component="h5"><strong>Orders</strong></Typography>
        {(selectedFilters?.length > 0 || orderFilters?.orderStartDate || orderFilters?.orderEndDate) &&
          <Button variant="outlined" onClick={clearAllFilters}>
            Clear
          </Button>
        }
      </Box>
      <Box pt={0}>
        Filters: {selectedFilters?.map((filter, index) => (
          <Chip
            key={index}
            label={filter.label}
            onDelete={() => removeFilter(filter)}
            variant="outlined"
            color="primary"
            style={{ margin: '5px' }}
          />
        ))}
      </Box>
      <FormControl fullWidth margin="normal">
        <DateRangeFilter
          startDate={orderFilters?.orderStartDate || null}
          endDate={orderFilters?.orderEndDate || null}
          onApply={(range) => {
            handleDateRangeChange(range);
          }}
        />

        {/* <DateFilter onDateRangeChange={handleDateRangeChange} startDate={orderFilters?.orderStartDate} endDate={orderFilters?.orderEndDate} /> */}
      </FormControl>
      <SearchableDropdown sx={{mt:0.5}}
        label="Select City"
        options={cityList?.list || []}
        value={
          cityList?.list?.filter(city =>
            selectedFilters
              ?.filter(item => item?.type === 'cities')
              .some(sel => sel.label === city.city)
          ) || []
        }
        labelKey="city"
        valueKey="city"
        onChange={(selectedOptions) => handleSelectCity(selectedOptions)}
        onSearch={handleSearchCity}
        multiSelect={true}
      />
      <Box sx={{ marginTop: '18px' }}>
        <SearchableDropdown
          label="Select CSP"
          options={channelPartnerData?.list || []}
          value={
            channelPartnerData?.list?.filter(partner =>
              selectedFilters
                ?.filter(item => item?.type === 'channel_sales_partner')
                .some(sel => sel.label === partner['Channel Partner Name'])
            ) || []
          }
          multiSelect={true}
          labelKey="Channel Partner Name"
          valueKey="Channel Partner Name"
          onChange={(selectedOptions) =>
            handleSelectSalePartner(selectedOptions)
          }
        />

      </Box>
      {/* Payment Status */}
      <Box>
        <Typography variant="subtitle1" sx={{ fontWeight: 'bold' , pt:1}}>Payment Status</Typography>
        <Box>
          {paymentStatusList?.map((status) => (
            <FormControlLabel
              key={status?.status_name}
              control={
                <Checkbox
                  checked={selectedFilters.some(item => item.objId === status?.status_name && item.type === "payment_status")}
                  onChange={() => handleCheckboxChange({ ...status }, "payment_status", 'status_name', "status_name")}
                />
              }
              label={status?.status_name}
            />
          ))}
        </Box>
      </Box>

      {/* Delivery Status */}
      <Box>
        <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>Delivery Status</Typography>
        <Box>
          {orderStatusList?.map((status, index) => (
            <FormControlLabel
              key={index}
              control={
                <Checkbox
                  checked={selectedFilters.some(item => item.objId === status?.status_name && item.type === "order_status")}
                  onChange={() => handleCheckboxChange({ ...status }, "order_status", 'status_name', "status_name")}
                />
              }
              label={status?.status_name}
            />
          ))}
        </Box>
      </Box>
    </Box>
  );
};

export default OrdersFilters;
